import { useState } from 'react';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
function formatValue(key: string, val: unknown): string {
  if (key === 'country' && typeof val === 'string' && val.length === 2) {
    try { return regionNames.of(val) ?? val; } catch { return val; }
  }
  return String(val);
}

interface Props {
  nodeId: string;
  data: Record<string, unknown>;
  position: { x: number; y: number };
  onClose: () => void;
  onFocus: (nodeId: string) => void;
  onHide: (nodeId: string) => void;
  onAskCade?: (prompt: string) => void;
}

export function NodePopover({ nodeId, data, position, onClose, onFocus, onHide, onAskCade }: Props) {
  const type = (data.type as string) ?? 'unknown';
  const label = (data.label as string) ?? nodeId;
  const severity = data.severity as string | undefined;
  const aliases = data.aliases as string[] | undefined;

  // Ontology v2 read-flip (Phase 4): when the node carries v2 projection fields,
  // surface entity type / kind / layer + the identity_keys JSON explicitly, and
  // keep those keys out of the generic details list below.
  const v2Type = data.v2Type as string | undefined;
  const v2Kind = data.v2Kind as string | undefined;
  const v2Layer = data.v2Layer as string | undefined;
  const identityKeys = data.identityKeys as Record<string, unknown> | undefined;

  const V2_KEYS = ['v2Type', 'v2Kind', 'v2Layer', 'identityKeys', 'layerColor', 'sub'];
  const details = Object.entries(data).filter(([k]) => !['id', 'label', 'type', 'severity', 'aliases', ...V2_KEYS].includes(k));

  const alerts = (data.alerts as Array<{ title: string; severity?: string; source?: string; time?: string; mitre?: string }>) ?? [];
  const alertCount = (data.alertCount as number) ?? alerts.length;

  const [customQuestion, setCustomQuestion] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  return (
    <>
      <div className="node-popover-backdrop" onClick={onClose} />
      <div className="node-popover" style={{ left: position.x, top: position.y }}>
        <div className="node-popover-header">
          <span className={`node-popover-type type-${type}`}>{type}</span>
          <span className="node-popover-label">{label}</span>
          {severity && <span className={`status-badge sev-${severity}`}>{severity}</span>}
        </div>
        {v2Type && (
          <div className="node-popover-details node-popover-v2">
            <div className="node-popover-row">
              <span className="node-popover-key">v2 entity</span>
              <span className="node-popover-val">{v2Kind ? `${v2Type}/${v2Kind}` : v2Type}</span>
            </div>
            {v2Layer && (
              <div className="node-popover-row">
                <span className="node-popover-key">v2 layer</span>
                <span className="node-popover-val">{v2Layer}</span>
              </div>
            )}
            {identityKeys && (
              <div className="node-popover-row">
                <span className="node-popover-key">identity_keys</span>
                <span className="node-popover-val node-popover-json">{JSON.stringify(identityKeys)}</span>
              </div>
            )}
          </div>
        )}
        {details.length > 0 && (
          <div className="node-popover-details">
            {details.slice(0, 6).map(([key, val]) => (
              <div key={key} className="node-popover-row">
                <span className="node-popover-key">{key}</span>
                <span className="node-popover-val">{formatValue(key, val)}</span>
              </div>
            ))}
          </div>
        )}
        {aliases && aliases.length > 0 && (
          <div className="node-popover-aliases">
            <span className="node-popover-key">Also identified as</span>
            {aliases.map((alias) => (
              <span key={alias} className="node-popover-alias-chip">{alias}</span>
            ))}
          </div>
        )}
        {alertCount > 0 && (
          <div className="node-popover-alerts">
            <button
              className="node-popover-alerts-toggle"
              onClick={() => setAlertsExpanded(!alertsExpanded)}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                style={{ transform: alertsExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                <path d="M3 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="node-popover-alerts-icon">{'\u26A0'}</span>
              <span>{alertCount} alert{alertCount !== 1 ? 's' : ''}</span>
            </button>
            {alertsExpanded && (
              <div className="node-popover-alerts-list">
                {alerts.map((alert, i) => (
                  <div key={i} className="node-popover-alert-item">
                    <div className="node-popover-alert-header">
                      {alert.severity && (
                        <span className={`status-badge sev-${alert.severity}`}>{alert.severity}</span>
                      )}
                      <span className="node-popover-alert-title">{alert.title}</span>
                    </div>
                    <div className="node-popover-alert-meta">
                      {alert.source && <span>{alert.source}</span>}
                      {alert.mitre && <span className="node-popover-alert-mitre">{alert.mitre}</span>}
                      {alert.time && <span>{new Date(alert.time).toLocaleString()}</span>}
                    </div>
                    {onAskCade && (
                      <button
                        className="btn-link btn-sm"
                        onClick={() => {
                          onAskCade(`Tell me more about the "${alert.title}" alert on ${type} ${label}`);
                          onClose();
                        }}
                      >
                        Investigate this alert
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="node-popover-actions">
          {onAskCade && (
            <button
              className="btn-primary btn-sm"
              onClick={() => {
                onAskCade(`Tell me more about ${type} ${label}`);
                onClose();
              }}
            >
              Ask Cade
            </button>
          )}
          <button className="btn-secondary btn-sm" onClick={() => onFocus(nodeId)}>Focus</button>
          <button className="btn-secondary btn-sm" onClick={() => { onHide(nodeId); onClose(); }}>Hide</button>
        </div>

        {/* Custom question input */}
        {onAskCade && (
          !showCustomInput ? (
            <button
              className="node-popover-custom-toggle"
              onClick={() => setShowCustomInput(true)}
            >
              Ask a specific question about {label}...
            </button>
          ) : (
            <div className="node-popover-custom-input">
              <input
                type="text"
                placeholder={`e.g. "Was ${label} seen in other alerts?"`}
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customQuestion.trim()) {
                    onAskCade(`Regarding ${type} ${label}: ${customQuestion.trim()}`);
                    onClose();
                  }
                }}
                autoFocus
              />
              <button
                className="btn-primary btn-sm"
                disabled={!customQuestion.trim()}
                onClick={() => {
                  if (customQuestion.trim()) {
                    onAskCade(`Regarding ${type} ${label}: ${customQuestion.trim()}`);
                    onClose();
                  }
                }}
              >
                Ask
              </button>
            </div>
          )
        )}
      </div>
    </>
  );
}
